<template>
    <div class="step-content" :class="{ mobile: isMobile }">
        <div
        v-for="(block, index) in blocksData"
        :key="index"
        class="step-section">
        <!-- Created Info Box -->
            <infobox-generic v-if="block.showCreatedInfo" :label="block.infoBoxLabel"/>
            <!-- Divider -->
            <divider-generic
            :applyBorder="block.divider.applyBorder"
            :dividerSize="block.divider.dividerSize"
            :icon="block.divider.icon"
            :title="block.divider.title"/>
            <!-- Text Block -->
            <text-blocks-generic
            v-for="(params, index) in block.params.filter(obj => !obj.hidden)"
            class="step-text-block"
            :key="index"
            :params="params"
            :applyEllipsis="params.applyEllipsis"
            :mask="params.mask"
            :classes="block.classes"/>
            <!-- Second Infobox -->
            <infobox-generic
            v-if="block.showInfo"
            :label="t('transfers.internationalCorporate.step1.estimatedAmountNoteInstant', { link: 'https://www.bancobai.ao/pt/precario', target: '_blank'})"/>
        </div>
        <div class="step-section">
            <!-- Divider -->
            <divider-generic class="last-divider" dividerSize="medium"/>
            <div class="button-wrapper">
                <button-primary
                icon="transfer"
                iconPosition="right"
                :label="t('transfers.internationalCorporate.step3.newTransfer')"
                @clicked="emit('newTransfer')"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonPrimary from '@/components/generic/buttons/Primary'

/* Vue && Assets */
import { computed } from 'vue'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const {
    getAccountsBalances,
    getTransfersReasons,
    getOperationTypesList,
    getCategoriesList,
    getTransfersExpenses,
    getCurrenciesList,
    getProfileUnmaskedInfo,
    getCountriesList,
    getInternationalCorporateTypes,
    getInternationalBanks
} = mapGetters(moduleMapper.TRANSFERS_INTERNATIONAL_CORPORATE)

/** Props
@formData {Object} - Object with form data
@formTabsList {Array} - Tab List for Iban or Account Transfer
@applyOwnCustomerInfo {Boolean} Boolean controller to show Own Customer Info
@formTabsList {Array} - Tab List for Iban or Account Transfer
@responseData {Object} - Request Response
*/
const props = defineProps({
    formData: Object,
    formTabsList: Array,
    applyOwnCustomerInfo: Boolean,
    responseData: Array,
    isUSDCurrency: Boolean
})

const emit = defineEmits([
    'newTransfer'
])

const transferByIban = computed(() => {
    return props.formTabsList[0].selected
})

const blocksData = computed(() => {
    const transferDocumentTypesBlock = []

    props.formData.transferTypeDocuments.forEach(doc => {
        const documents = getInternationalCorporateTypes.value.find(types => types.paymentType === props.formData.transferType)?.documentTypes

        transferDocumentTypesBlock.push({
            label: documents.find(parsedDoc => parsedDoc.internationalTransferDocumentType === doc.id).description,
            content: doc.file.name,
            applyEllipsis: true
        })

        if (doc.id === 'MERCHANDISE_INVOICE') {
            transferDocumentTypesBlock.push({
                label: t('transfers.internationalCorporate.genericLabels.invoiceNumber'),
                content: props.formData.invoiceNumber
            },
            {
                label: t('transfers.internationalCorporate.genericLabels.invoiceDate'),
                content: getDate(props.formData.invoiceDate),
                classes: 'capitalize'
            })
        }
    })

    return [
        {
            divider: { icon: 'person', title: t('transfers.internationalCorporate.step2.dividers.originAccount'), applyBorder: false, dividerSize: 'medium' },
            showCreatedInfo: true,
            infoBoxLabel: props.responseData.additionalMessage ? createdPendingMessage() : null,
            params: [
                {
                    label: t('transfers.internationalCorporate.genericLabels.account'),
                    content: getAccountsBalances.value.find(obj => obj.id === props.formData.originAccount).accountNumber,
                    mask: ACCOUNT_NUMBER
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.ownEmail'),
                    content: props.formData.ownEmail
                }
            ]
        },
        {
            divider: { icon: 'document', title: t('transfers.internationalCorporate.step2.dividers.operationData'), applyBorder: true, dividerSize: 'medium' },
            params: [
                {
                    label: t('transfers.internationalCorporate.genericLabels.transferReasons'),
                    content: getTransfersReasons.value.find(reason => reason.id === props.formData.internationalTransferReasonLeafId).description
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.operationType'),
                    content: getOperationTypesList.value.find(reason => reason.id === props.formData.internationalTransferOperationTypeId).description
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.operationCategory'),
                    content: getCategoriesList.value.find(reason => reason.id === props.formData.internationalTransferPaymentCategoryId).description
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.transferExpense'),
                    content: getTransfersExpenses.value.find(reason => reason.id === props.formData.internationalTransferExpenseId).description
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.transferType'),
                    content: getInternationalCorporateTypes.value.find(obj => obj.paymentType === props.formData.transferType).paymentTypeDescription
                },
                ...transferDocumentTypesBlock
            ]
        },
        {
            divider: { icon: 'bills', title: t('transfers.internationalCorporate.step2.dividers.amount'), applyBorder: true, dividerSize: 'medium' },
            showInfo: true,
            params: [
                {
                    label: t('transfers.internationalCorporate.genericLabels.currency'),
					content: getCurrenciesList.value.find(currency => currency.id === props.formData.internationalTransferCurrencyId).externalId
                },
                {
                    label: t('transfers.instantBAI.genericLabels.amountToTransfer'),
                    content: amountFormat(props.formData.amount)
                },
                {
                    label: t('transfers.internationalCorporate.genericLabels.estimatedAmount'),
                    content: amountFormat(props.formData.estimatedAmount, props.formData.accountCurrency)
                }
            ]
        },
        {
            divider: { icon: 'group', title: t('transfers.internationalCorporate.step2.dividers.destination'), applyBorder: true, dividerSize: 'medium' },
            params: [
                {
					label: t('transfers.internationalCorporate.genericLabels.bank'),
					content: props.formData.bankListId ? getInternationalBanks.value.find(bank => bank.id === props.formData.bankListId).bankName : '-',
                    hidden: !props.isUSDCurrency
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.destinationIbanAccount'),
					content: transferByIban.value ? props.formData.accountInternationalNumber : props.formData.accountNumber
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.swift'),
					content: props.formData.swiftCode || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.beneficaryName'),
					content: (props.applyOwnCustomerInfo ? getProfileUnmaskedInfo.value.personalData.name : props.formData.name) || '-'
				},
				{
					label: t('transfers.internationalCorporate.genericLabels.nif'),
					content: (props.applyOwnCustomerInfo ? getProfileUnmaskedInfo.value.fiscalDocument.fiscalNumber : props.formData.fiscalDocumentNumber) || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.phoneNumber'),
					content: props.formData.phoneNumber || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.email'),
					content: props.formData.email || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.country'),
					content: getCountriesList.value.find(obj => obj.id === props.formData.internationalTransferCountryId).description || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.city'),
					content: props.formData.city || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.address'),
					content: props.formData.address || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.doorNumber'),
					content: props.formData.door || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.floor'),
					content: props.formData.floor || '-'
				},
                {
					label: t('transfers.internationalCorporate.genericLabels.postalCode'),
					content: props.formData.zipCode || '-'
				}
            ]
        }
    ]
})

const createdPendingMessage = () => {
    if (props.responseData.additionalMessage) {
        const messages = props.responseData.additionalMessage.split('|')
        if (messages[1]) {
            return `${messages[0]}${messages[1]}`
        }
        return messages[0]
    }
    return null
}

</script>

<style lang="scss" scoped>
.step-content {
    width: 100%;
    margin-top: 24px;

    display: flex;
    flex-direction: column;
    align-items: center;

    .step-section {
        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;

        &:not(:last-of-type) {
            margin-bottom: 24px;
        }

        .last-divider {
            padding-top: unset;
        }

        .button-wrapper {
            width: 100%;

            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 16px;

            .favorite-button {
                max-width: 154px;
            }
        }
    }

    &.mobile {
        :deep(.button-tertiary) {
            margin-bottom: 16px;
        }

        .step-section {
            padding: 16px 16px 24px 16px;
            border-radius: 12px;

            background-color: var(--white);
            box-shadow: 0 3px 6px 1px rgba(0, 50, 90, 0.16);

            &:not(:last-of-type) {
                margin-bottom: 16px;
            }
        }
    }
}
</style>
